import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {ROUTES} from '../../constants/routes';
import {useOpportunity} from '../../contexts/opportunity.provider';
import {Opportunity} from '../../models/opportunity';
import {Button, ButtonVariant} from '../../components/button';
import {DashboardLayout} from '../../components';
import {Text, TextColor, TextVariant} from '../../components/typography/text';
import {ApplicantsPanel} from './components/applicants-panel';
import {OpportunityDetailsSidebar} from './components';

const OpportunityDetail: React.FC = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {getOpportunityById} = useOpportunity();
  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) {
        setError('No opportunity ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const opportunityResponse = await getOpportunityById(id);
        setOpportunity(opportunityResponse);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, getOpportunityById]);

  const handleBackToOpportunities = () => {
    navigate(ROUTES.OPPORTUNITIES);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <Text variant={TextVariant.BODY} color={TextColor.MUTED}>
            Loading opportunity...
          </Text>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !opportunity) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-full space-y-4">
          <Text variant={TextVariant.BODY} className="text-red-500">
            {error || 'Opportunity not found'}
          </Text>
          <Button variant={ButtonVariant.Secondary} onClick={handleBackToOpportunities}>
            Back to Opportunities
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      breadcrumbs={[
        {label: 'Dashboard', path: '/dashboard'},
        {label: 'Opportunities', path: '/opportunities'},
        {
          label: opportunity?.jobTitle || '',
          path: `/opportunities/${opportunity?.id}`,
        },
      ]}>
      <div className="h-full flex flex-col">
        <div className="flex-1 overflow-hidden">
          <div className="flex h-full py-4">
            <OpportunityDetailsSidebar opportunity={opportunity} restrictedToCommunities={opportunity.restrictedToCommunities ?? []} />
            <ApplicantsPanel opportunity={opportunity} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OpportunityDetail;
